// Arcane building prefabs: dark shrine, mana font, observatory and the two
// portals. Adapted from adventure-craft.
import { box, hollowBox } from './buildings_shared.js';
import type { StructurePrefab } from './types.js';

// Dark Shrine — chaos-aligned temple variant.
export const darkShrine: StructurePrefab = {
  name: 'Dark Shrine',
  blocks: [
    ...box(-4, 0, -4, 9, 1, 9, '#1a1018', 'stone'),
    ...hollowBox(-4, 1, -4, 9, 5, 9, '#2a1828', 'stone'),
    ...box(-3, 1, -5, 7, 1, 1, '#1a1018', 'stone'),
    ...box(-5, 1, -3, 1, 4, 7, '#1a1018', 'stone'),
    ...box(4, 1, -3, 1, 4, 7, '#1a1018', 'stone'),
    ...box(-4, 6, -4, 9, 1, 9, '#331122', 'stone'),
    ...box(-2, 7, -2, 5, 1, 5, '#441133', 'stone'),
    ...box(-1, 8, -1, 3, 2, 3, '#551144', 'stone'),
    { x: 0, y: 10, z: 0, color: '#ff1144', type: 'stone' },
    { x: -3, y: 1, z: -3, color: '#ff1144', type: 'stone' },
    { x: 3, y: 1, z: 3, color: '#ff1144', type: 'stone' },
  ],
};

// Mana Font — crystalline fountain of arcane energy.
export const manaFont: StructurePrefab = {
  name: 'Mana Font',
  blocks: [
    ...box(-3, 0, -3, 7, 1, 7, '#303050', 'stone'),
    ...box(-2, 1, -2, 5, 1, 5, '#353560', 'stone'),
    ...box(-1, 2, -1, 3, 1, 3, '#404080', 'stone'),
    { x: 0, y: 3, z: 0, color: '#5555ff', type: 'stone' },
    { x: 0, y: 4, z: 0, color: '#6666ff', type: 'stone' },
    { x: 0, y: 5, z: 0, color: '#8888ff', type: 'stone' },
    { x: 1, y: 3, z: 0, color: '#4444cc', type: 'stone' },
    { x: -1, y: 3, z: 0, color: '#4444cc', type: 'stone' },
    { x: 0, y: 3, z: 1, color: '#4444cc', type: 'stone' },
    { x: 0, y: 3, z: -1, color: '#4444cc', type: 'stone' },
    // Floating crystal shards above
    { x: 1, y: 6, z: 0, color: '#aaaaff', type: 'glass' },
    { x: -1, y: 6, z: 0, color: '#aaaaff', type: 'glass' },
    { x: 0, y: 7, z: 1, color: '#ccccff', type: 'glass' },
    { x: 0, y: 6, z: -1, color: '#aaaaff', type: 'glass' },
  ],
};

// Observatory — tall tower with a domed top.
export const observatory: StructurePrefab = {
  name: 'Observatory',
  blocks: [
    ...box(-3, 0, -3, 7, 1, 7, '#a09080', 'stone'),
    ...hollowBox(-3, 1, -2, 7, 8, 5, '#c0b0a0', 'stone'),
    // Dome
    ...box(-3, 9, -3, 7, 1, 7, '#8090b0', 'stone'),
    ...box(-2, 10, -2, 5, 1, 5, '#7080a0', 'stone'),
    ...box(-1, 11, -1, 3, 1, 3, '#607090', 'stone'),
    { x: 0, y: 12, z: 0, color: '#4488cc', type: 'glass' },
    // Telescope
    { x: 0, y: 10, z: 2, color: '#cccc88', type: 'wood' },
    { x: 0, y: 11, z: 2, color: '#dddd88', type: 'wood' },
    { x: 0, y: 11, z: 3, color: '#4488cc', type: 'glass' },
    // Windows
    { x: 3, y: 3, z: 0, color: '#4488cc', type: 'glass' },
    { x: -3, y: 3, z: 0, color: '#4488cc', type: 'glass' },
    { x: 0, y: 3, z: -2, color: '#4488cc', type: 'glass' },
    { x: 3, y: 6, z: 0, color: '#4488cc', type: 'glass' },
    { x: -3, y: 6, z: 0, color: '#4488cc', type: 'glass' },
  ],
};

// Portal of Light — golden archway with bright glow.
export const portalLight: StructurePrefab = {
  name: 'Portal of Light',
  blocks: [
    ...box(-3, 0, -3, 7, 1, 7, '#c0b090', 'stone'),
    // Arch pillars
    ...box(-3, 1, -1, 1, 6, 3, '#d4c8a0', 'stone'),
    ...box(3, 1, -1, 1, 6, 3, '#d4c8a0', 'stone'),
    // Arch top
    ...box(-2, 7, -1, 5, 1, 3, '#d4c8a0', 'stone'),
    ...box(-1, 8, -1, 3, 1, 3, '#ffd700', 'stone'),
    // Portal glow (center of arch)
    { x: 0, y: 1, z: 0, color: '#ffffff', type: 'glass' },
    { x: 0, y: 2, z: 0, color: '#ffffcc', type: 'glass' },
    { x: 0, y: 3, z: 0, color: '#ffffaa', type: 'glass' },
    { x: 0, y: 4, z: 0, color: '#ffffcc', type: 'glass' },
    { x: 0, y: 5, z: 0, color: '#ffffff', type: 'glass' },
    // Gold accents
    { x: -2, y: 1, z: 2, color: '#ffd700', type: 'stone' },
    { x: 2, y: 1, z: 2, color: '#ffd700', type: 'stone' },
    { x: -2, y: 5, z: 2, color: '#ffd700', type: 'stone' },
    { x: 2, y: 5, z: 2, color: '#ffd700', type: 'stone' },
  ],
};

// Portal of Darkness — dark archway with red/crimson glow.
export const portalDark: StructurePrefab = {
  name: 'Portal of Darkness',
  blocks: [
    ...box(-3, 0, -3, 7, 1, 7, '#201820', 'stone'),
    // Arch pillars
    ...box(-3, 1, -1, 1, 6, 3, '#301828', 'stone'),
    ...box(3, 1, -1, 1, 6, 3, '#301828', 'stone'),
    // Arch top
    ...box(-2, 7, -1, 5, 1, 3, '#301828', 'stone'),
    ...box(-1, 8, -1, 3, 1, 3, '#441133', 'stone'),
    // Portal glow (center of arch)
    { x: 0, y: 1, z: 0, color: '#440022', type: 'glass' },
    { x: 0, y: 2, z: 0, color: '#660033', type: 'glass' },
    { x: 0, y: 3, z: 0, color: '#880044', type: 'glass' },
    { x: 0, y: 4, z: 0, color: '#660033', type: 'glass' },
    { x: 0, y: 5, z: 0, color: '#440022', type: 'glass' },
    // Crimson accents
    { x: -2, y: 1, z: 2, color: '#cc1144', type: 'stone' },
    { x: 2, y: 1, z: 2, color: '#cc1144', type: 'stone' },
    { x: -2, y: 5, z: 2, color: '#cc1144', type: 'stone' },
    { x: 2, y: 5, z: 2, color: '#cc1144', type: 'stone' },
  ],
};
